/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX, ReactNode } from 'react';
import{
  Fragment,
  useCallback,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Box,
  Step,
  StepConnector,
  StepLabel,
  Stepper
} from '@mui/material';
import styled from 'styled-components';

import { PAGE_ROUTES, TAB_TITLE } from '../../utils/constants';
import TitleSection from './TitleSection';

import './index.css';


const headerIconTextStyle = `
  @media only screen and (max-width: 700px) {
    .header-icon-text-desktop { display: none; }
    .header-icon-mobile-pad { padding: var(--spacing-base, 8px); display: inline-flex; }
    .header-support-desktop { display: none; }
    .header-support-mobile { display: inline-flex; }
    .header-dashboard-desktop { display: none; }
    .header-dashboard-mobile { display: inline-flex; }
  }
  @media only screen and (min-width: 701px) {
    .header-icon-mobile-pad { padding: 0; margin-right: 0; }
    .header-support-mobile { display: none; }
    .header-dashboard-mobile { display: none; }
  }
`;

export const SavingMessage = styled.p`
  color: var(--neutral-1000, #101c16);
  margin-right: var(--spacing-lg, 16px);
  margin-left: 5px;
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: 'Helvetica Now For Cargill', Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-decoration: none;
`;

export const NavigationLayer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media only screen and (max-width: 700px) {
    display: inline;
  }
`;

export const ReviewTab = styled(Link)`
  padding: 7px 20px;
  width: 15%;
  min-width: 100px;
  max-width: 150px;
  background-color: var(--cargill-green);
  color: var(--white);
  font-size: 11px;
  text-decoration: none;
  text-align: center;

  &:hover {
    opacity: 0.7;
  }

  @media only screen and (max-width: 700px) {
    display: none;
  }
`;

/* ----------------------------------
   Review button / stepper
---------------------------------- */

interface ReviewButtonProps {
  formPages?: any[];
  isOnlyUtility?: boolean;
  currentPage?: string;
  t: (key: string) => string;
}

export function ReviewButton({
  formPages = [],
  isOnlyUtility,
  currentPage,
  t
}: ReviewButtonProps): JSX.Element {
  const steps = [
    ...formPages.filter(
      page => page.route !== PAGE_ROUTES.review
    ),
    { route: PAGE_ROUTES.review }
  ];

  const activeStep = Math.max(
    0,
    steps.findIndex(
      page => page.route === currentPage
    )
  );

  const prefetchReviewPage = () => {
    import('../../features/ReviewPage').catch(
      () => undefined
    );
  };

  return (
    <Fragment>
      {formPages.length > 1 && !isOnlyUtility && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 0,
            flexWrap: 'nowrap',
            paddingTop: '24px',
            paddingBottom: '24px'
          }}
        >
          <Stepper
            className="rc-header-review-stepper"
            orientation="horizontal"
            activeStep={activeStep}
            alternativeLabel
            connector={<StepConnector />}
            sx={{ width: '100%', p: 0, m: 0 }}
          >
            {steps.map((page, index) => {
              const isReview =
                page.route === PAGE_ROUTES.review;

              let label: string;
              if (isReview) {
                label = t('buttons.review');
              } else if (
                TAB_TITLE[page.route as keyof typeof TAB_TITLE] !=
                null
              ) {
                label = t(
                  `tabTitles.${
                    TAB_TITLE[
                      page.route as keyof typeof TAB_TITLE
                    ]
                  }`
                );
              } else {
                label = page.route;
              }

              const isCompleted = index < activeStep;

              return (
                <Step
                  key={page.route}
                  index={index}
                  completed={isCompleted}
                >
                  <StepLabel>
                    <Link
                      to={page.route}
                      style={{
                        textDecoration: 'none',
                        color:
                          'var(--sprout-text-primary, #101C16)',
                        font:
                          'var(--sprout-font-body1-bold)'
                      }}
                      onMouseEnter={
                        isReview
                          ? prefetchReviewPage
                          : undefined
                      }
                      onFocus={
                        isReview
                          ? prefetchReviewPage
                          : undefined
                      }
                    >
                      {label}
                    </Link>
                    {page?.extraLabelText
                      ? ` ${page.extraLabelText}`
                      : null}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      )}
    </Fragment>
  );
}

interface HeaderProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  formCompletionTime?: ReactNode;
  pathname?: string;
  recordType?: string;
  isOnlyUtility?: boolean;
  formPages?: any[];
  mobilePreStepper?: ReactNode;
  contentWidth?: string | number;
}

function Header({
  title,
  subtitle,
  formCompletionTime,
  pathname = '',
  recordType,
  isOnlyUtility,
  formPages = [],
  mobilePreStepper,
  contentWidth
}: HeaderProps): JSX.Element {
  const currentPage = pathname
    ?.split('/')
    .reverse()[0];

  const [scroll, setScroll] = useState(0);
  const [dxpTokenCheck, setDxpTokenCheck] =
    useState<boolean | string | undefined>(
      false
    );

  const handleScroll = useCallback(
    () => setScroll(window.scrollY),
    []
  );

  const { t } = useTranslation();

  useEffect(() => {
    // Okta token manager stores the DXP session as a single JSON object keyed by 'dxp-token-storage'
    let dxpAccessToken: string | undefined;
    try {
      const tokenStorage = window.localStorage.getItem(
        'dxp-token-storage'
      );
      dxpAccessToken = tokenStorage
        ? JSON.parse(tokenStorage)?.accessToken
            ?.accessToken
        : undefined;
    } catch {
      dxpAccessToken = undefined;
    }

    setDxpTokenCheck(dxpAccessToken);

    window.addEventListener('scroll', handleScroll);
    return () =>
      window.removeEventListener(
        'scroll',
        handleScroll
      );
  }, [handleScroll, dxpTokenCheck]);

  const shouldShowStepper =
    formPages.length > 1 && !isOnlyUtility;

  return (
    <Fragment>
      <style>{headerIconTextStyle}</style>

      {currentPage !== PAGE_ROUTES.dashboard &&
        currentPage !== PAGE_ROUTES.help && (
          <Box
            sx={{
              width: '100%',
              maxWidth: {
                xs: '100vw',
                md: contentWidth
              },
              marginRight: 'auto'
            }}
          >
            <TitleSection
              title={title}
              subtitle={subtitle}
              formCompletionTime={formCompletionTime}
              isOnlyUtility={isOnlyUtility}
              formPages={formPages}
              scroll={!!scroll}
              currentPage={currentPage}
              recordType={recordType}
            />

            {mobilePreStepper && (
              <Box sx={{ mt: { xs: '16px', md: 0 } }}>
                {mobilePreStepper}
              </Box>
            )}

            <ReviewButton
              formPages={formPages}
              isOnlyUtility={isOnlyUtility}
              currentPage={currentPage}
              t={t}
            />
          </Box>
        )}

      {currentPage !== PAGE_ROUTES.dashboard &&
        currentPage !== PAGE_ROUTES.help &&
        currentPage !== PAGE_ROUTES.tracker &&
        currentPage?.toLowerCase() !==
          PAGE_ROUTES.faqPage &&
        !shouldShowStepper && (
          <Box sx={{ width: '100%', mb: '24px' }} />
        )}
    </Fragment>
  );
}

export default Header;